/**
 * Foto de satélite ao fundo nos quadros sem a imagem de base (a NOAA parou, loop/semNoaa.ts).
 *
 * Sem a base, o mapa ficava preto atrás dos focos, estações e rios. Nesses
 * quadros, e só neles, a foto do EOxCloudless (a mesma do zoom mais próximo)
 * aparece por baixo também nos zooms afastados.
 *
 * Uma camada à parte, que só cobre os zooms em que a camada da foto
 * (fundoLayer.ts) não é desenhada (`minResolution` = `FUNDO_MAX_RESOLUTION`),
 * SEM ruas e nomes: nos zooms afastados os nomes do fundo repetiriam os do
 * próprio mapa ("Manaus" duas vezes). A camada do zoom mais próximo, a
 * transição do GOES para a foto (`goesFade`) e o fundo do "Próximos dias"
 * (previsao/ocultar.ts, que muda o `maxResolution` dela) ficam como estavam.
 * Fora dos trechos sem a base esta camada fica escondida, e o OpenLayers não
 * pede nenhum bloco para ela. No "Próximos dias" também: lá o fundo já é a foto.
 */
import type OlMap from 'ol/Map';
import BaseVectorLayer from 'ol/layer/BaseVector';
import TileLayer from 'ol/layer/Tile';
import type XYZ from 'ol/source/XYZ';
import { subscribeSelector } from '../lib/store';
import { usePlanStore } from '../loop/plan';
import { trechoDoQuadro, type TrechoSemBase } from '../loop/semNoaa';
import { usePrevisaoStore } from '../previsao/store';
import { useGoesStore } from '../state/goes';
import { useTimeStore } from '../state/time';
import { definirFotoAoFundo } from './contraste';
import { FUNDO_MAX_RESOLUTION, fotoSource } from './fundoLayer';
import { Z } from './zIndex';

/** A foto entra ao fundo nos zooms afastados? Só sem a base no quadro, com ela ligada e fora do "Próximos dias". */
export function fundoSemNoaaLigado(
  trechos: readonly TrechoSemBase[] | null | undefined,
  currentTime: number | null,
  baseVisivel: boolean,
  modoPrevisao: boolean,
): boolean {
  return baseVisivel && !modoPrevisao && trechoDoQuadro(trechos, currentTime) !== null;
}

export class FundoSemNoaa {
  readonly layer: TileLayer<XYZ>;
  private readonly unsubs: Array<() => void>;

  constructor(private readonly map: OlMap) {
    this.layer = new TileLayer({
      source: fotoSource(),
      // Até onde a camada da foto começa: dali para dentro é ela, como sempre.
      minResolution: FUNDO_MAX_RESOLUTION,
      zIndex: Z.FUNDO,
      preload: 0,
      visible: false,
      className: 'ol-layer fundo-layer',
    });
    this.layer.set('title', 'Foto de satélite (fundo sem a imagem da NOAA)');
    map.addLayer(this.layer);
    const avaliar = () => {
      const ligar = fundoSemNoaaLigado(
        usePlanStore.getState().plan?.trechosSemBase,
        useTimeStore.getState().currentTime,
        !!useGoesStore.getState().layers[0]?.visible,
        usePrevisaoStore.getState().modo,
      );
      if (this.layer.getVisible() !== ligar) this.layer.setVisible(ligar);
      // Os símbolos ganham (ou perdem) o selo de contraste da foto também nos zooms afastados:
      // o estilo só é recalculado quando a camada muda de revisão.
      if (definirFotoAoFundo(ligar)) {
        for (const l of this.map.getAllLayers()) if (l instanceof BaseVectorLayer) l.changed();
      }
    };
    this.unsubs = [
      subscribeSelector(usePlanStore, (s) => s.plan?.trechosSemBase, avaliar),
      subscribeSelector(useTimeStore, (s) => s.currentTime, avaliar),
      subscribeSelector(useGoesStore, (s) => !!s.layers[0]?.visible, avaliar),
      subscribeSelector(usePrevisaoStore, (s) => s.modo, avaliar),
    ];
    avaliar();
  }

  dispose(): void {
    this.unsubs.forEach((u) => u());
    definirFotoAoFundo(false);
    this.map.removeLayer(this.layer);
    this.layer.getSource()?.dispose();
    this.layer.dispose();
  }
}
